import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/contexts/AuthContext';
import styles from '@/components/auth/AuthPages.module.css';

export default function SetupPage() {
  const [secret, setSecret] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { completeSetup } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 12) {
      setError('The password must be at least 12 characters.');
      return;
    }

    setSubmitting(true);
    try {
      await completeSetup(secret, displayName, email, password);
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to complete setup.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={styles.authPage}>
      <div className={styles.authContent}>
        <div className={styles.brand}>MegaTraderDX</div>
        <Card header="Set up your administrator account">
          <form className={styles.form} onSubmit={handleSubmit}>
            {error && <Alert variant="danger">{error}</Alert>}
            <p className={styles.hint}>
              Enter the one-time setup secret from the application startup logs.
            </p>
            <Input
              label="Setup secret"
              type="password"
              autoComplete="off"
              autoFocus
              required
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
            />
            <Input
              label="Display name"
              autoComplete="name"
              required
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
            <Input
              label="Email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Input
              label="Password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <Input
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
            <Button className={styles.submit} type="submit" disabled={submitting}>
              {submitting ? 'Creating administrator...' : 'Complete setup'}
            </Button>
          </form>
        </Card>
      </div>
    </main>
  );
}
